import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type DraftRefsTarget = "main" | "subthread";

export type DraftRefsByTarget<TRef> = Record<DraftRefsTarget, TRef[]>;

export interface AppendDraftRefsResult {
    addedCount: number;
    skippedOverLimitCount: number;
}

export interface DraftRefsState<TRef> {
    draftRefsByTarget: DraftRefsByTarget<TRef>;
    appendDraftRefs: (
        target: DraftRefsTarget,
        refs: TRef[],
    ) => AppendDraftRefsResult;
    clearDraftRefs: (target: DraftRefsTarget) => void;
    removeDraftRef: (target: DraftRefsTarget, index: number) => void;
}

function createEmptyDraftRefsByTarget<TRef>(): DraftRefsByTarget<TRef> {
    return { main: [], subthread: [] };
}

/**
 * Per-composer draft chat references for one task: deduped by `getDedupKey`,
 * capped at `maxRefs` per target, and reset whenever `taskId` changes.
 * `getDedupKey` must be referentially stable (e.g. a module-level function).
 */
export function useDraftRefsByTarget<TRef>({
    taskId,
    maxRefs,
    getDedupKey,
}: {
    taskId: string;
    maxRefs: number;
    getDedupKey: (ref: TRef) => string;
}): DraftRefsState<TRef> {
    const [draftRefsByTarget, setDraftRefsByTarget] = useState<
        DraftRefsByTarget<TRef>
    >(createEmptyDraftRefsByTarget);
    const draftRefsByTargetRef =
        useRef<DraftRefsByTarget<TRef>>(draftRefsByTarget);
    draftRefsByTargetRef.current = draftRefsByTarget;

    useEffect(() => {
        setDraftRefsByTarget((prev) =>
            prev.main.length === 0 && prev.subthread.length === 0
                ? prev
                : createEmptyDraftRefsByTarget(),
        );
    }, [taskId]);

    const clearDraftRefs = useCallback((target: DraftRefsTarget) => {
        setDraftRefsByTarget((prev) =>
            prev[target].length === 0 ? prev : { ...prev, [target]: [] },
        );
    }, []);

    const removeDraftRef = useCallback(
        (target: DraftRefsTarget, index: number) =>
            setDraftRefsByTarget((prev) => ({
                ...prev,
                [target]: prev[target].filter((_, i) => i !== index),
            })),
        [],
    );

    const appendDraftRefs = useCallback(
        (
            target: DraftRefsTarget,
            refsToAppend: TRef[],
        ): AppendDraftRefsResult => {
            if (refsToAppend.length === 0) {
                return { addedCount: 0, skippedOverLimitCount: 0 };
            }
            const existing = draftRefsByTargetRef.current[target];
            const seenKeys = new Set(existing.map(getDedupKey));
            const deduped: TRef[] = [];
            for (const ref of refsToAppend) {
                const key = getDedupKey(ref);
                if (seenKeys.has(key)) {
                    continue;
                }
                seenKeys.add(key);
                deduped.push(ref);
            }
            const availableSlots = Math.max(0, maxRefs - existing.length);
            const refsToAdd = deduped.slice(0, availableSlots);
            const skippedOverLimitCount = deduped.length - refsToAdd.length;
            if (refsToAdd.length > 0) {
                const next: DraftRefsByTarget<TRef> = {
                    ...draftRefsByTargetRef.current,
                    [target]: [...existing, ...refsToAdd],
                };
                draftRefsByTargetRef.current = next;
                setDraftRefsByTarget(next);
            }
            return { addedCount: refsToAdd.length, skippedOverLimitCount };
        },
        [getDedupKey, maxRefs],
    );

    return useMemo(
        () => ({
            draftRefsByTarget,
            appendDraftRefs,
            clearDraftRefs,
            removeDraftRef,
        }),
        [draftRefsByTarget, appendDraftRefs, clearDraftRefs, removeDraftRef],
    );
}

export function useTargetDraftRefs<TRef>({
    draftRefsState,
    target,
}: {
    draftRefsState: DraftRefsState<TRef> | null;
    target: DraftRefsTarget;
}) {
    const draftRefs = draftRefsState?.draftRefsByTarget[target];
    const removeDraftRef = draftRefsState?.removeDraftRef;

    return useMemo(
        () => ({
            draftRefs,
            onRemoveDraftRef: removeDraftRef
                ? (index: number) => removeDraftRef(target, index)
                : undefined,
        }),
        [draftRefs, removeDraftRef, target],
    );
}
